import {
  Box,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverContent,
  Spinner,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { InputGroup, InputGroupProps } from './InputGroup'

export type AutocompleteItem = {
  itemText?: string | React.ReactNode
  inputText: string
  value: string
  customData?: any
}

export type AutocompleteProps = {
  refetch: (value: string) => Promise<AutocompleteItem[]>
  onChange: (value: string) => void
  onSelect?: (item: AutocompleteItem) => void
  validFromLength?: number // minimalni delka platne hodnoty (napr ulice musi mit alespon 3 znaky nez se zacne vyhledavat)
  initLoad?: boolean
  value: string
} & Omit<InputGroupProps, 'onChange' | 'onSelect' | 'value'>

export const Autocomplete = forwardRef(function Autocomplete(
  {
    refetch,
    onChange,
    onSelect,
    validFromLength = 1,
    defaultValue,
    value,
    initLoad = false,
    ...props
  }: AutocompleteProps,
  ref,
) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>()

  // kvuli logovani a debugovani odkud se otevira a zavira
  const closePopover = useCallback(() => onClose(), [onClose])
  const openPopover = useCallback(() => onOpen(), [onOpen])

  const isValidInput = useCallback(
    (val: string | undefined | null) => {
      return (val?.length ?? 0) >= validFromLength
    },
    [validFromLength],
  )

  // Váš vlastní hook pro načítání dat
  const [dataSource, setDataSource] = useState<AutocompleteItem[]>([])

  const loadData = (value: string, openList: boolean = true) => {
    if (isValidInput(value)) {
      refetch(value)
        .then((data) => setDataSource(data))
        .finally(() => {
          setIsLoading(false)
        })

      if (openList) openPopover()
    }
  }

  useEffect(() => {
    if (initLoad) loadData(value, false)
  }, [])

  // Debounced callback - 200ms zpozdeni nez se zacne nacitat (aby se pri rychlem psani nehledalo po kazdem pismenku)
  const handleChange = useDebouncedCallback((value) => {
    loadData(value)
  }, 200)

  // vyber z dropdownu
  const handleSelect = useCallback(
    (item: AutocompleteItem) => {
      onChange(item.inputText)
      onSelect?.(item)
      closePopover()
    },
    [onChange, onSelect, closePopover],
  )

  // pri kliku mimo chceme naseptavac zavrit
  useOutsideClick({
    ref: inputRef as any,
    handler: (e) => closePopover(),
  })

  useEffect(() => {
    // pokud se napr odmazava a uz neni nadale validni input, tak se zavre naseptavac
    if (!isValidInput(value)) closePopover()

    if (!isValidInput(value)) setIsLoading(false)
  }, [value, closePopover, setIsLoading, isValidInput])

  return (
    <Popover isOpen={isOpen} matchWidth closeOnBlur closeOnEsc initialFocusRef={inputRef as any}>
      <PopoverAnchor>
        <InputGroup
          value={value}
          autoComplete="off"
          rightElement={isLoading && <Spinner />}
          onChange={(event) => {
            const val = event.target.value
            onChange(val)
            handleChange(val)
            isValidInput(val) && setIsLoading(true)
          }}
          onFocus={(event) => {
            if (!isOpen && isValidInput(event.target.value)) {
              openPopover()
            }
          }}
          ref={inputRef}
          {...props}
        />
      </PopoverAnchor>

      {dataSource.length > 0 && (
        <PopoverContent
          sx={{
            display: 'grid',
            justifyContent: 'stretch',
            maxH: '500px',
            overflow: 'auto',
          }}
        >
          <PopoverArrow />
          {dataSource.map((suggestion, index) => {
            return (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  px: 2,
                  py: 1,
                  justifyContent: 'start',
                  cursor: 'pointer',
                  textAlign: 'start',
                  _hover: { backgroundColor: 'gray.100' },
                }}
                onClick={() => handleSelect(suggestion)}
              >
                <Text fontSize="sm" fontWeight={suggestion.inputText == value ? 'bold' : ''}>
                  {suggestion.itemText ?? suggestion.inputText}
                </Text>
              </Box>
            )
          })}
        </PopoverContent>
      )}
    </Popover>
  )
})
